import { createWithEqualityFn } from 'zustand/traditional';

import { baseService } from '@services/base.service';
import useNotificationStore from '@store/NotificationStore';

type FavoriteCareersResponse = { onetsoc_code: string; favorite: boolean }[];

interface FavoriteCareersStore {
  favoriteCareers: { [key: string]: boolean };
  isLoading: boolean;
  getFavoriteCareers: () => void;
  toggleFavoriteCareer: (socCode: string, userId: number) => void;
  addFavoriteCareer: (socCode: string, userId: number) => Promise<void>;
  deleteFavoriteCareer: (socCode: string, userId: number) => Promise<void>;
}

const initialState = {
  favoriteCareers: {},
  isLoading: false,
};

export const useFavoriteCareersStore = createWithEqualityFn<FavoriteCareersStore>()(
  (set, get) => ({
    ...initialState,

    getFavoriteCareers: async () => {
      set({ isLoading: true });

      const favoriteCareersResponse = await baseService.getAsync<FavoriteCareersResponse>(`/favorite-careers`);

      const preparedData = favoriteCareersResponse.reduce(
        (previousValue, currentValue) => {
          // eslint-disable-next-line no-param-reassign
          previousValue[currentValue.onetsoc_code] = currentValue.favorite;

          return previousValue;
        },
        {} as Record<string, boolean>,
      );

      set({
        favoriteCareers: preparedData,
        isLoading: false,
      });
    },

    toggleFavoriteCareer: async (socCode: string, userId: number) => {
      const state = get();

      const isSelectedCareer = state.favoriteCareers[socCode];

      set({
        favoriteCareers: { ...state.favoriteCareers, [socCode]: !isSelectedCareer },
      });

      if (!isSelectedCareer) {
        await state.addFavoriteCareer(socCode, userId);
      } else {
        await state.deleteFavoriteCareer(socCode, userId);
      }
    },

    addFavoriteCareer: async (socCode: string, userId: number) => {
      await baseService.postAsync(`/favorite-career/${socCode}`, null, {
        params: {
          career: {
            favorite: true,
            onetsoc_code: socCode,
            user_id: userId,
          },
        },
      });

      useNotificationStore.getState().notify({
        message: `Saved to Favorites`,
        severity: 'info',
      });
    },

    deleteFavoriteCareer: async (socCode: string, userId: number) => {
      await baseService.deleteAsync(`/favorite-career/${socCode}`, {
        params: {
          career: {
            favorite: false,
            onetsoc_code: socCode,
            user_id: userId,
          },
        },
      });

      useNotificationStore.getState().notify({
        message: `Removed from Favorites`,
        severity: 'info',
      });
    },
  }),
  Object.is,
);
